/**
 * Product Tree View - Hierarchical tree visualization for products
 * Displays: Segment → Category → Product with expand/collapse
 */

import { useState, useMemo } from 'react';
import type { ProductSegment, Product } from '../../types';

interface ProductTreeViewProps {
  segments: ProductSegment[];
  onProductSelect: (product: Product) => void;
}

// Segment color mapping
const segmentColors: Record<string, { bg: string; border: string; text: string }> = {
  'Pharmaceutical Distribution': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  'Medical-Surgical': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  'Healthcare Technology': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  'Point of Care Solutions': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
  'Practice Management': { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700' },
  'Data & Analytics': { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700' },
};

const defaultColors = { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' };

interface TreeNodeProps {
  label: string;
  level: number;
  isExpanded: boolean;
  onToggle: () => void;
  hasChildren: boolean;
  colors: { bg: string; border: string; text: string };
  badge?: string;
  children?: React.ReactNode;
}

function TreeNode({
  label,
  level,
  isExpanded,
  onToggle,
  hasChildren,
  colors,
  badge,
  children,
}: TreeNodeProps) {
  const indentClass = level === 0 ? '' : level === 1 ? 'ml-6' : 'ml-12';
  const contentId = `tree-content-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className={indentClass} data-testid={level === 0 ? 'tree-segment' : 'tree-category'}>
      <button
        onClick={onToggle}
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-controls={hasChildren ? contentId : undefined}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border ${colors.border} ${colors.bg} hover:shadow-sm transition-all text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1`}
      >
        {hasChildren && (
          <svg
            className={`w-4 h-4 ${colors.text} transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
        {!hasChildren && <div className="w-4" aria-hidden="true" />}
        <span className={`font-medium ${colors.text}`}>{label}</span>
        {badge && (
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium" aria-label={`${badge} products`}>
            {badge}
          </span>
        )}
      </button>
      {isExpanded && children && (
        <div id={contentId} className="mt-1 space-y-1" role="group" aria-label={`${label} contents`}>
          {children}
        </div>
      )}
    </div>
  );
}

interface ProductLeafProps {
  product: Product;
  colors: { bg: string; border: string; text: string };
  onClick: () => void;
}

function ProductLeaf({ product, colors, onClick }: ProductLeafProps) {
  return (
    <button
      onClick={onClick}
      aria-label={`View details for ${product.name}${product.is_flagship ? ' (Flagship product)' : ''}`}
      data-testid="tree-product-leaf"
      className={`ml-12 w-[calc(100%-3rem)] flex items-center gap-2 px-3 py-2 rounded-lg border ${colors.border} bg-white hover:${colors.bg} hover:shadow-sm transition-all text-left group focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1`}
    >
      <div className="w-2 h-2 rounded-full bg-gray-300 group-hover:bg-primary-400 transition-colors" aria-hidden="true" />
      <span className="text-gray-700 group-hover:text-gray-900">{product.name}</span>
      {product.is_flagship && (
        <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 font-medium">
          Flagship
        </span>
      )}
      {product.target_customers && product.target_customers.length > 0 && (
        <span className="ml-auto text-xs text-gray-400">
          {product.target_customers.slice(0, 2).map(tc => tc.replace('_', ' ')).join(', ')}
        </span>
      )}
      <svg
        className="w-4 h-4 text-gray-300 group-hover:text-primary-500 transition-colors"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

export function ProductTreeView({ segments, onProductSelect }: ProductTreeViewProps) {
  // Track expanded state for segments and categories
  const [expandedSegments, setExpandedSegments] = useState<Set<string>>(
    () => new Set(segments.map(s => s.name)) // All expanded by default
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Group products by category within each segment
  const segmentData = useMemo(() => {
    return segments.map(segment => {
      const categoriesMap = new Map<string, Product[]>();

      segment.products.forEach(product => {
        const category = product.category || 'Other';
        if (!categoriesMap.has(category)) {
          categoriesMap.set(category, []);
        }
        categoriesMap.get(category)!.push(product);
      });

      return {
        ...segment,
        categories: Array.from(categoriesMap.entries()).map(([name, products]) => ({
          name,
          products,
        })),
      };
    });
  }, [segments]);

  const toggleSegment = (segmentName: string) => {
    setExpandedSegments(prev => {
      const next = new Set(prev);
      if (next.has(segmentName)) {
        next.delete(segmentName);
      } else {
        next.add(segmentName);
      }
      return next;
    });
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  const totalProducts = segments.reduce((sum, s) => sum + s.products.length, 0);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between px-1">
        <div className="text-sm text-gray-500">
          {totalProducts} products across {segments.length} segments
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setExpandedSegments(new Set(segments.map(s => s.name)))}
            className="text-xs text-primary-600 hover:text-primary-800"
          >
            Expand All
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => {
              setExpandedSegments(new Set());
              setExpandedCategories(new Set());
            }}
            className="text-xs text-primary-600 hover:text-primary-800"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Tree */}
      <div className="space-y-2">
        {segmentData.map(segment => {
          const colors = segmentColors[segment.name] || defaultColors;
          const segmentExpanded = expandedSegments.has(segment.name);

          return (
            <TreeNode
              key={segment.name}
              label={segment.name}
              level={0}
              isExpanded={segmentExpanded}
              onToggle={() => toggleSegment(segment.name)}
              hasChildren={segment.categories.length > 0}
              colors={colors}
              badge={`${segment.products.length}`}
            >
              {segment.categories.map(category => {
                const categoryKey = `${segment.name}:${category.name}`;
                const categoryExpanded = expandedCategories.has(categoryKey);
                const hasMultipleProducts = category.products.length > 1;

                // If only one product in category, show it directly
                if (!hasMultipleProducts) {
                  const singleProduct = category.products[0];
                  if (!singleProduct) return null;
                  return (
                    <ProductLeaf
                      key={singleProduct.id}
                      product={singleProduct}
                      colors={colors}
                      onClick={() => onProductSelect(singleProduct)}
                    />
                  );
                }

                return (
                  <TreeNode
                    key={categoryKey}
                    label={category.name}
                    level={1}
                    isExpanded={categoryExpanded}
                    onToggle={() => toggleCategory(categoryKey)}
                    hasChildren={true}
                    colors={{ ...colors, bg: 'bg-white' }}
                    badge={`${category.products.length}`}
                  >
                    {category.products.map(product => (
                      <ProductLeaf
                        key={product.id}
                        product={product}
                        colors={colors}
                        onClick={() => onProductSelect(product)}
                      />
                    ))}
                  </TreeNode>
                );
              })}
            </TreeNode>
          );
        })}
      </div>

      {/* Empty state */}
      {segments.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No products found.</p>
          <p className="text-sm text-gray-400 mt-2">
            Product data is being collected.
          </p>
        </div>
      )}
    </div>
  );
}

export default ProductTreeView;
